import { CommonOperations, contains } from '../../../store/permissions';
import { PPMFeatures, ProductType, Subscription, isAzureAdAvailable } from '../../../store/Tenant';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { UserState } from '../../../store/User';
import { Section } from '../../../entities/Metadata';
import SectionableSettingsPage from '../SectionableSettingsPage';
import StyleSettingsTooltip from '../../common/sectionsControl/uiControls/styleSettingsControl/StyleSettingsTooltip';
import FeaturesSettingsTooltip from '../../common/sectionsControl/uiControls/featuresSettingsControl/FeaturesSettingsTooltip';
import OrganizationAuthenticationSettingsTooltip from '../../common/sectionsControl/uiControls/organizationAuthenticationSettingsControl/OrganizationAuthenticationSettingsTooltip';
import GlobalSettingsTooltip from '../../common/sectionsControl/uiControls/globalSettingsControl/GlobalSettingsTooltip';
import SyncSettingsTooltip from '../../common/sectionsControl/uiControls/syncSettingsControl/SyncSettingsTooltip';
import PresentationModeSettingsTooltip from '../../common/sectionsControl/uiControls/presentationModeSettingsControl/PresentationModeSettingsTooltip';
import NotificationSettingsTooltip from '../../common/sectionsControl/uiControls/notificationSettingsControl/NotificationSettingsTooltip';
import ResourcePlanningSettingsTooltip from '../../common/sectionsControl/uiControls/resourcePlanningSettingsControl/ResourcePlanningSettingsTooltip';
import AzureADSyncSettingsTooltip from '../../common/sectionsControl/uiControls/azureADSyncSettingsControl/AzureADSyncSettingsTooltip';
import AzureADUsersSyncSettingsTooltip from '../../common/sectionsControl/uiControls/azureADUsersSyncSettingsControl/AzureADUsersSyncSettingsTooltip';
import ExtensionsSettingsTooltip from '../../common/sectionsControl/uiControls/extensionsSettingsControl/ExtensionsSettingsTooltip';
import AiInsightsSettingsTooltip from '../../common/sectionsControl/uiControls/aiInsightsSettingsControl/AiInsightsSettingsTooltip';
import AiAgentsSettingsTooltip from '../../common/sectionsControl/uiControls/aiAgentsSettingsControl/AiAgentsSettingsTooltip';
import OktaAuthenticationSettingsTooltip from '../../common/sectionsControl/uiControls/oktaAuthenticationSettingsControl/OktaAuthenticationSettingsTooltip';

export const TenantSettingsSectionKeys = {
    featuresAndModules: "featuresAndModules",
    styleSettings: "styleSettings",
    organizationAuthenticationSettings: "organizationAuthenticationSettings",
    globalSettings: "globalSettings",
    dataSynchronizationSettings: "dataSynchronizationSettings",
    notifications: "notifications",
    resourcePlanningSettings: "resourcePlanningSettings",
    defaultProjectSettings: "defaultProjectSettings",
    presentationModeSettings: "presentationModeSettings",
    azureADSyncSettings: "azureADSyncSettings",
    azureADUsersSyncSettings: "azureADUsersSyncSettings",
    extensionsSettings: "extensionsSettings",
    aiAgentsSettings: "aiAgentsSettings",
    aiInsightsSettings: "aiInsightsSettings",
    oktaAuthenticationSettings: "oktaAuthenticationSettings",
} as const;

type TenantSettingsSectionKey = typeof TenantSettingsSectionKeys[keyof typeof TenantSettingsSectionKeys];

const tenantSettingsSectionMap: Record<TenantSettingsSectionKey,
    { name: string, iconName: string, tooltip: JSX.Element | undefined, controlName: string, navigationLabel?: string }> = {
    [TenantSettingsSectionKeys.featuresAndModules]: {
        name: 'Features & Modules',
        iconName: 'WebComponents',
        tooltip: <FeaturesSettingsTooltip />,
        controlName: 'FeaturesSettingsControl',
    },
    [TenantSettingsSectionKeys.styleSettings]: {
        name: 'Style',
        iconName: 'PPMXStyle',
        tooltip: <StyleSettingsTooltip />,
        controlName: 'StyleSettingsControl'
    },
    [TenantSettingsSectionKeys.organizationAuthenticationSettings]: {
        name: 'Organization Authentication',
        navigationLabel: 'Authentication',
        iconName: 'Shield',
        tooltip: <OrganizationAuthenticationSettingsTooltip />,
        controlName: 'OrganizationAuthenticationSettingsControl'
    },
    [TenantSettingsSectionKeys.oktaAuthenticationSettings]: {
        name: 'Okta Authentication Settings',
        navigationLabel: 'Okta',
        iconName: 'PPMXOkta',
        tooltip: <OktaAuthenticationSettingsTooltip />,
        controlName: 'OktaAuthenticationSettingsControl'
    },
    [TenantSettingsSectionKeys.globalSettings]: {
        name: 'Global Settings',
        iconName: 'Globe',
        tooltip: <GlobalSettingsTooltip />,
        controlName: 'GlobalSettingsControl'
    },
    [TenantSettingsSectionKeys.dataSynchronizationSettings]: {
        name: 'Data Synchronization',
        navigationLabel: 'Data Sync',
        iconName: 'Sync',
        tooltip: <SyncSettingsTooltip />,
        controlName: 'SyncSettingsControl'
    },
    [TenantSettingsSectionKeys.notifications]: {
        name: 'Notifications',
        iconName: 'PPMXNotification',
        tooltip: <NotificationSettingsTooltip />,
        controlName: 'NotificationSettingsControl'
    },
    [TenantSettingsSectionKeys.resourcePlanningSettings]: {
        name: 'Resource Planning',
        iconName: 'DateTimeMirrored',
        tooltip: <ResourcePlanningSettingsTooltip />,
        controlName: 'ResourcePlanningSettingsControl'
    },
    [TenantSettingsSectionKeys.defaultProjectSettings]: {
        name: 'Default Project Settings',
        navigationLabel: 'Project Settings',
        iconName: 'FabricReportLibrary',
        tooltip: undefined,
        controlName: 'DefaultProjectSettingsControl'
    },
    [TenantSettingsSectionKeys.presentationModeSettings]: {
        name: 'Presentation Mode',
        iconName: 'AnalyticsView',
        tooltip: <PresentationModeSettingsTooltip />,
        controlName: 'PresentationModeSettingsControl'
    },
    [TenantSettingsSectionKeys.azureADSyncSettings]: {
        name: 'Azure Active Directory Resource Synchronization',
        navigationLabel: 'Resource Sync',
        iconName: 'AADLogo',
        tooltip: <AzureADSyncSettingsTooltip />,
        controlName: 'AzureADSyncSettingsControl'
    },
    [TenantSettingsSectionKeys.azureADUsersSyncSettings]: {
        name: 'Azure Active Directory User Synchronization',
        navigationLabel: 'User Sync',
        iconName: 'Contact',
        tooltip: <AzureADUsersSyncSettingsTooltip />,
        controlName: 'AzureADUsersSyncSettingsControl'
    },
    [TenantSettingsSectionKeys.extensionsSettings]: {
        name: 'Extensions',
        iconName: 'Embed',
        tooltip: <ExtensionsSettingsTooltip />,
        controlName: 'ExtensionsSettingsControl'
    },
    [TenantSettingsSectionKeys.aiInsightsSettings]: {
        name: 'PPM Insights AI',
        iconName: 'D365CustomerInsights',
        tooltip: <AiInsightsSettingsTooltip />,
        controlName: 'AiInsightsSettingsControl',
    },
    [TenantSettingsSectionKeys.aiAgentsSettings]: {
        name: 'AI Agents',
        iconName: 'PPMXAiGenerated',
        tooltip: <AiAgentsSettingsTooltip />,
        controlName: 'AiAgentsSettingsControl'
    }
}
  
const buildSectionSettings = (key: TenantSettingsSectionKey, isSelected = true, isLockable = false, isOpen = true, isAccessible = true, uiControlsSections = []): Section => ({
    id: key,
    name: tenantSettingsSectionMap[key].name,
    navigationLabel: tenantSettingsSectionMap[key].navigationLabel,
    settings: {
        iconName: tenantSettingsSectionMap[key].iconName,
        tooltip: tenantSettingsSectionMap[key].tooltip,
    },
    isSelected: isSelected,
    isOpen: isOpen,
    isAccessible: isAccessible,
    uiControls: [{
        id: key,
        type: tenantSettingsSectionMap[key].controlName,
        settings: uiControlsSections
    }],
    isLockable: isLockable
});

type Props = {
    subscription: Subscription;
    user: UserState;
    productType: ProductType;
}

const tenantFeaturesCapabilities = [
    PPMFeatures.TimeTracking,
    PPMFeatures.TimeTrackingEnterprise, 
    PPMFeatures.PrivateProjects, 
    PPMFeatures.PresentationMode, 
    PPMFeatures.PresentationModeUltra
]

const TenantSettings = (props: Props) => {
    const { subscription, user, productType } = props;
    const isFeaturesAndModulesSettingsAvailable = Subscription.containsAny({ features: subscription.allFeatures }, tenantFeaturesCapabilities);
    const isDataSynchronizationSettingsAvailable = contains(user.permissions.common, CommonOperations.ScheduleManage) && Subscription.hasSyncableIntegration(subscription);
    const isResourcePlanningSettingsAvailable = Subscription.contains(subscription, PPMFeatures.ResourceManagement) && Subscription.contains(subscription, PPMFeatures.PortfolioManagement);
    const isPresentationModeSettingsAvaialble = Subscription.containsAny(subscription, [PPMFeatures.PresentationMode, PPMFeatures.PresentationModeUltra]);
    const isAzureAdSettingsAvailable = isAzureAdAvailable(productType);
    const isExtensionSettingsAvailable = Subscription.contains(subscription, PPMFeatures.Extensions);
    const isAiInsightsAvailable = Subscription.contains(subscription, PPMFeatures.AiInsights);
    const isAiAgentsAvailable = Subscription.contains(subscription, PPMFeatures.AiAgents);
    const isOktaAuthAvailable = Subscription.contains(subscription, PPMFeatures.OktaAuth);

    const sections: Section[] = [
        buildSectionSettings(TenantSettingsSectionKeys.featuresAndModules, isFeaturesAndModulesSettingsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.styleSettings),
        buildSectionSettings(TenantSettingsSectionKeys.organizationAuthenticationSettings),
        buildSectionSettings(TenantSettingsSectionKeys.oktaAuthenticationSettings, isOktaAuthAvailable, true),
        buildSectionSettings(TenantSettingsSectionKeys.globalSettings),
        buildSectionSettings(TenantSettingsSectionKeys.dataSynchronizationSettings, isDataSynchronizationSettingsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.notifications),
        buildSectionSettings(TenantSettingsSectionKeys.resourcePlanningSettings, isResourcePlanningSettingsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.defaultProjectSettings),
        buildSectionSettings(TenantSettingsSectionKeys.presentationModeSettings, isPresentationModeSettingsAvaialble),
        buildSectionSettings(TenantSettingsSectionKeys.aiInsightsSettings, isAiInsightsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.aiAgentsSettings, isAiAgentsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.azureADSyncSettings, isAzureAdSettingsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.azureADUsersSyncSettings, isAzureAdSettingsAvailable),
        buildSectionSettings(TenantSettingsSectionKeys.extensionsSettings, isExtensionSettingsAvailable),
    ];

    return <SectionableSettingsPage
        title="Tenant Settings"
        requiredPermission={CommonOperations.ScheduleView}
        sections={sections}
        settingsKey='tenantSettings' />;
}

const mapStateToProps = (_: ApplicationState) => ({
    subscription: _.tenant.subscription,
    aiAgents: _.aiAgents,
    user: _.user,
    productType: _.tenant.subscription.productType
});
export default connect(mapStateToProps)(TenantSettings);